import React from 'react';
import { Link as MuiLink, SxProps, Theme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { parseTextWithLinks, TextSegment } from '../../utils/linkParser';

interface LinkedTextProps {
  text: string;
  sx?: SxProps<Theme>;
  component?: React.ElementType;
  variant?: 'body1' | 'body2' | 'h6' | 'caption';
}

/**
 * Component that renders text with support for inline links
 * 
 * Usage in JSON:
 * "description": "Check out our [API Documentation](https://example.com) for more details."
 * "description": "Navigate to [Settings Page](/settings) to configure."
 * 
 * Link syntax:
 * - [Link Text](https://example.com) - External link (opens in new tab)
 * - [Link Text](/internal/path) - Internal link (uses React Router)
 * - [Link Text](url){external} - Force external link behavior
 * - [Link Text](url){internal} - Force internal link behavior
 */
const LinkedText: React.FC<LinkedTextProps> = ({ 
  text, 
  sx = {}, 
  component = 'span',
  variant 
}) => {
  const segments: TextSegment[] = parseTextWithLinks(text);

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === 'link' && segment.url) {
          if (segment.external) {
            // External link - opens in new tab
            return (
              <MuiLink
                key={index}
                href={segment.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#1976d2',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#1565c0'
                  },
                  ...sx
                }}
              >
                {segment.content}
              </MuiLink>
            );
          } else {
            // Internal link - uses React Router
            return (
              <MuiLink
                key={index}
                component={RouterLink}
                to={segment.url}
                sx={{
                  color: '#1976d2',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#1565c0'
                  },
                  ...sx
                }}
              >
                {segment.content}
              </MuiLink>
            );
          }
        } else {
          // Regular text
          return <React.Fragment key={index}>{segment.content}</React.Fragment>;
        }
      })}
    </>
  );
};

export default LinkedText;
